import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { 
  ChevronLeft, 
  Clock, 
  Users, 
  MapPin, 
  Award, 
  Calendar,
  CheckCircle,
  Play,
  Download,
  Star,
  X,
  Sparkles,
  Microscope,
  Building
} from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
const labImage1 = "/assets/f9564ab801a1786dc9a7f34bcbbceeaa941ff959.png";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ScheduleAndFAQ } from "./schedule/ScheduleAndFAQ";
import { scheduleData } from "../data/breeders";
import { ContactForm } from "./forms/ContactForm";

interface PresencialPageProps {
  onNavigateBack: () => void;
  onNavigateToCheckout: (courseId: string) => void;
  onNavigateToCursoOnline: () => void;
  onNavigateToCursoAvanzado: () => void;
  onNavigateToNuestrosCursos: () => void;
}

export function PresencialPage({ onNavigateBack, onNavigateToCheckout, onNavigateToCursoOnline, onNavigateToCursoAvanzado, onNavigateToNuestrosCursos }: PresencialPageProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  return (
    <div ref={containerRef} className="relative min-h-screen bg-black text-white">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-green-500/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateBack}
                className="text-white hover:text-green-500 mr-4"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <img
                src={rebelLogo}
                alt="Rebel Genomics"
                className="w-8 h-8 mr-2"
              />
              <span className="text-white text-lg">RebelGenomics</span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => onNavigateToCheckout('presencial')}
              >
                {t("courses.presencial.enrollNow")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 z-0"
        >
          <ImageWithFallback
            src="/assets/presencial.png"
            alt="Modern Laboratory Workspace"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 via-black/60 to-black/80"></div>
        </motion.div>

        <div className="relative z-10 container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge className="bg-green-600 text-white mb-6 text-sm px-4 py-2">
                {t("courses.presencial.badge")}
              </Badge>
              
              <h1 className="text-5xl md:text-7xl mb-6 text-green-500">
                {t("courses.presencial.heroTitle")}
              </h1>

              <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
                {t("courses.presencial.heroDescription")}
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <Clock className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.presencial.duration")}</div>

                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <Users className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.presencial.students")}</div>

                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <MapPin className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.presencial.location")}</div>

                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <Award className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.presencial.certificate")}</div>

                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white text-xl px-12 py-6 rounded-full"
                  onClick={() => onNavigateToCheckout('presencial')}
                >
                  {t("courses.presencial.enrollButton")}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-black hover:bg-white hover:text-black text-xl px-12 py-6 rounded-full"
                >
                  {t("courses.presencial.viewProgram")}
                </Button>
              </div>
            </motion.div>

            {/* Video Preview */}
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <Card className="bg-white/10 backdrop-blur-sm border-green-500/30 p-6 rounded-3xl">
                <div className="relative">
                  <img
                    src={labImage1}
                    alt="Laboratorio RebelGenomics"
                    className="w-full h-64 object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-black/30 rounded-2xl flex items-center justify-center">
                    <Button
                      size="lg"
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border-2 border-white/50 rounded-full p-6"
                    >
                      <Play className="w-8 h-8 text-white" />
                    </Button>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <h3 className="text-xl mb-2 text-green-400">{t("courses.presencial.limitedGroup")}</h3>
                  <p className="text-gray-400">{t("courses.presencial.nextEdition")}</p>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - What you'll learn */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <h2 className="text-4xl md:text-5xl mb-8 text-green-500">
                {t("courses.presencial.whatYouLearn")}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {[
                  t("courses.presencial.feature1"),
                  t("courses.presencial.feature2"),
                  t("courses.presencial.feature3"),
                  t("courses.presencial.feature4"),
                  t("courses.presencial.feature5"),
                  t("courses.presencial.feature6"),
                  t("courses.presencial.feature7"),
                  t("courses.presencial.feature8")
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              {/* Course Schedule */}
              <h3 className="text-3xl mb-6 text-white">{t("courses.presencial.schedule")}</h3>
              <div className="space-y-4">
                {[
                  {
                    module: "Módulo 1",
                    title: t("modules.presencial.module1.title"),
                    duration: t("modules.presencial.module1.duration"),
                    topics: t("modules.presencial.module1.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 2", 
                    title: t("modules.presencial.module2.title"),
                    duration: t("modules.presencial.module2.duration"),
                    topics: t("modules.presencial.module2.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 3",
                    title: t("modules.presencial.module3.title"),
                    duration: t("modules.presencial.module3.duration"), 
                    topics: t("modules.presencial.module3.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 4",
                    title: t("modules.presencial.module4.title"),
                    duration: t("modules.presencial.module4.duration"),
                    topics: t("modules.presencial.module4.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 5",
                    title: t("modules.presencial.module5.title"),
                    duration: t("modules.presencial.module5.duration"),
                    topics: t("modules.presencial.module5.topics", { returnObjects: true })
                  }
                ].map((module, index) => (
                  <Card key={index} className="bg-white/5 border-green-500/20 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Badge className="bg-green-600 text-white mb-2">{module.module}</Badge>
                        <h4 className="text-xl text-white">{module.title}</h4>
                      </div>
                      <span className="text-green-400">{module.duration}</span>
                    </div>
                    <ul className="space-y-1">
                      {(module.topics as string[]).map((topic, topicIndex) => (
                        <li key={topicIndex} className="text-gray-400 text-sm">• {topic}</li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Course Info & Registration */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white/10 backdrop-blur-sm border-green-500/30 p-8 rounded-3xl sticky top-24">
                <div className="text-center mb-6">
                  <div className="text-3xl text-white mb-2">1499€</div>
                  <div className="text-gray-400 line-through">1799€</div>
                  <Badge className="bg-green-600 text-white">{t("courses.presencial.limitedGroup")}</Badge>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-white">{t("courses.presencial.nextEdition")}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-white">{t("courses.presencial.time")}</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-white">{t("courses.presencial.students")}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-white">{t("courses.presencial.location")}</span>
                  </div>
                </div>

                <Button
                  size="lg"
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6 mb-4"
                  onClick={() => onNavigateToCheckout('presencial')}
                >
                  {t("courses.presencial.enrollButton")}
                </Button>

                <div className="text-center text-sm text-gray-400 mb-6">
                  {t("courses.presencial.guarantee")}
                </div>

                <div className="border-t border-gray-700 pt-6">
                  <h4 className="text-lg mb-4 text-white">{t("courses.presencial.includes")}</h4>
                  <div className="space-y-2">
                    {[
                      t("courses.presencial.bullet1"),
                      t("courses.presencial.bullet2"),
                      t("courses.presencial.bullet3"),
                      t("courses.presencial.bullet4"),
                      t("courses.presencial.bullet5"),
                      t("courses.presencial.bullet6"),
                      t("courses.presencial.bullet7"),
                      t("courses.presencial.bullet8"),
                      t("courses.presencial.bullet9"),
                      t("courses.presencial.bullet10")
                    ].map((item, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructor Info */}
                <div className="border-t border-gray-700 pt-6 mt-6">
                  <h4 className="text-lg mb-4 text-white">TU INSTRUCTOR</h4>
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white text-xl">RG</span>
                    </div>
                    <div>
                      <h5 className="text-white">Pedro Cifuentes</h5>
                      <p className="text-gray-400 text-sm">Director de Laboratorio</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-4">
                    +7 años de experiencia dirigiendo laboratorios de cultivo in vitro.
                    Especialista en formación práctica y transferencia de conocimiento.
                  </p>
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    ))}
                    <span className="text-gray-400 text-sm ml-2">(5.0/5 - 15 reseñas)</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                    onClick={onNavigateToNuestrosCursos}
                  >
                    Conoce más sobre el instructor
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schedule and FAQ Section */}
      <ScheduleAndFAQ
        dates={scheduleData.dates}
        faq={scheduleData.faq}
        onCta={onNavigateToCheckout}
      />

      {/* Contact Form Section */}
      <ContactForm 
        onSubmit={async (data) => {
          // TODO: Implementar envío del formulario de contacto
          console.log('Contact form data:', data);
        }}
      />
    </div>
  );
}